import {
  type Content,
  type LayoutContentTypeID,
  type ModalContent,
} from '@/features/cms/components/CMS/types'
import { isProduction } from '@/features/shared/utils/environment'
import { logError } from '@/features/shared/utils/logger'
import { isOnServer } from '@shared/constants/util'
import { routes } from '@shared/constants/routes'
import { cmsComponents } from '@/features/cms/components/CMS/CMSComponents'

function shouldLogMissingCmsData() {
  return (
    // missing components in Preview is expected
    // we will only log for code running on the client
    isProduction && // only care about production
    !isOnServer() &&
    !window.location.pathname.includes(routes.CMS_PREVIEW_PAGE.url)
  )
}

export function getComponentForContentTypeId({
  contentTypeId,
  layoutEagerComponents,
}: {
  contentTypeId: Content['content_type_id'] | ModalContent['content_type_id']
  layoutEagerComponents?: Partial<typeof cmsComponents>
}) {
  const components: typeof cmsComponents = {
    ...cmsComponents,
    ...layoutEagerComponents,
  }
  if (contentTypeId in components) {
    return components[contentTypeId]
  }

  if (shouldLogMissingCmsData()) {
    logError('CMS: Component not found for content_type_id', { contentTypeId })
  }

  return null
}

export const cmsLayoutTypes: string[] = [
  'page_layout',
  'global_home_page_layout',
  'retailer_home_page_layout',
  'marketplace_page_layout',
] satisfies LayoutContentTypeID[]

export function isValidLayoutContentTypeID(contentTypeId: LayoutContentTypeID) {
  const hasCmsLayoutType = cmsLayoutTypes.includes(contentTypeId)

  if (!hasCmsLayoutType && shouldLogMissingCmsData()) {
    logError('CMS: Layout not found for content_type_id', { contentTypeId })
  }

  return hasCmsLayoutType
}
